import { useState } from 'react';
import { useSelector } from '@legendapp/state/react';
import { notes$, getDeletedNotes, restoreNote, permanentlyDeleteNote, updateNote } from '@/stores/notes$';
import { snippets$, getDeletedSnippets, restoreSnippet, permanentlyDeleteSnippet } from '@/stores/snippets$';
import { cn } from '@/lib/utils';
import { RotateCcw, Trash2, FileText, Code2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import Modal from './Modal';

export default function TrashView() {
  const [activeTab, setActiveTab] = useState('notes'); // 'notes' or 'snippets'
  const [confirmDelete, setConfirmDelete] = useState(null); // { type, id, title, details }

  // Get deleted notes with snippet counts
  const deletedNotes = useSelector(() => {
    const notes = getDeletedNotes();
    const snippetsObj = snippets$.get();
    const snippetsArray = snippetsObj && typeof snippetsObj === 'object' && !Array.isArray(snippetsObj)
      ? Object.values(snippetsObj)
      : [];

    return notes.map(note => {
      const allSnippets = snippetsArray.filter(s => s && s.note_id === note.id);
      const activeSnippets = allSnippets.filter(s => !s.deleted);
      const deletedSnippets = allSnippets.filter(s => s.deleted);

      return {
        ...note,
        activeSnippetCount: activeSnippets.length,
        deletedSnippetCount: deletedSnippets.length,
      };
    });
  });

  // Get deleted snippets with parent note info
  // Look up parent note from all notes (active + deleted) since a snippet
  // can be deleted while its parent note is still active
  const deletedSnippets = useSelector(() => {
    const snippets = getDeletedSnippets();
    const notesObj = notes$.get();

    return snippets.map(snippet => {
      const parentNote = notesObj[snippet.note_id];
      return {
        ...snippet,
        parentNoteTitle: parentNote?.title || 'Unknown Note',
        parentNoteDeleted: parentNote?.deleted || false,
      };
    });
  });

  const handleRestoreNote = (noteId) => {
    restoreNote(noteId);
  };

  const handleRestoreSnippet = (snippetId) => {
    restoreSnippet(snippetId, (noteId) => {
      updateNote(noteId, { updated_at: new Date().toISOString() });
    });
  };

  const updateNoteTimestamp = (noteId) => {
    updateNote(noteId, { updated_at: new Date().toISOString() });
  };

  const handleConfirmedDelete = () => {
    if (confirmDelete?.type === 'note') {
      permanentlyDeleteNote(confirmDelete.id);
    } else if (confirmDelete?.type === 'snippet') {
      permanentlyDeleteSnippet(confirmDelete.id, updateNoteTimestamp);
    } else if (confirmDelete?.type === 'empty') {
      if (activeTab === 'notes') {
        deletedNotes.forEach(note => permanentlyDeleteNote(note.id));
      } else {
        deletedSnippets.forEach(snippet => permanentlyDeleteSnippet(snippet.id, updateNoteTimestamp));
      }
    }
    setConfirmDelete(null);
  };

  return (
    <>
      <div className="w-full md:w-[300px] border-r border-border h-full min-h-0 flex flex-col bg-background/50 backdrop-blur-xl">
        {/* Header with tabs */}
        <div className="p-3 border-b border-border/50 bg-background/95 z-10 backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Trash</h2>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center gap-1 p-1 bg-muted/40 rounded-lg">
            <button
              onClick={() => setActiveTab('notes')}
              className={cn(
                "flex-1 h-8 px-3 rounded-md text-xs font-medium transition-all",
                activeTab === 'notes'
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <div className="flex items-center justify-center gap-1.5">
                <FileText size={14} />
                <span>Notes</span>
                <span className="text-muted-foreground">({deletedNotes.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('snippets')}
              className={cn(
                "flex-1 h-8 px-3 rounded-md text-xs font-medium transition-all",
                activeTab === 'snippets'
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <div className="flex items-center justify-center gap-1.5">
                <Code2 size={14} />
                <span>Snippets</span>
                <span className="text-muted-foreground">({deletedSnippets.length})</span>
              </div>
            </button>
          </div>

          {/* Empty trash button */}
          {((activeTab === 'notes' && deletedNotes.length > 0) || (activeTab === 'snippets' && deletedSnippets.length > 0)) && (
            <button
              onClick={() => setConfirmDelete({
                type: 'empty',
                count: activeTab === 'notes' ? deletedNotes.length : deletedSnippets.length,
                itemType: activeTab
              })}
              className="w-full mt-2 h-8 px-3 text-xs font-medium text-destructive hover:bg-destructive/10 rounded-lg transition-all"
            >
              Empty {activeTab === 'notes' ? 'Notes' : 'Snippets'} Trash
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {activeTab === 'notes' ? (
            // Notes section
            deletedNotes.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                <p className="text-xs font-medium opacity-50">No deleted notes</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {deletedNotes.map(note => (
                  <div key={note.id} className="relative group">
                    <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-muted-foreground/20" />

                    <div className="p-4 hover:bg-muted/30 transition-colors pl-5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-muted-foreground line-clamp-1 mb-1">
                            {note.title}
                          </h3>
                          <p className="text-xs text-muted-foreground/60 mb-1">
                            Deleted {formatDistanceToNow(new Date(note.deleted_at), { addSuffix: true })}
                          </p>
                          <p className="text-xs text-muted-foreground/50">
                            {note.activeSnippetCount + note.deletedSnippetCount} version{note.activeSnippetCount + note.deletedSnippetCount !== 1 ? 's' : ''}
                            {note.deletedSnippetCount > 0 && (
                              <span> ({note.activeSnippetCount} active, {note.deletedSnippetCount} deleted)</span>
                            )}
                          </p>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleRestoreNote(note.id)}
                            className="p-1.5 rounded transition-all text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                            title="Restore note"
                          >
                            <RotateCcw size={14} />
                          </button>
                          <button
                            onClick={() => setConfirmDelete({
                              type: 'note',
                              id: note.id,
                              title: note.title,
                              snippetCount: note.activeSnippetCount + note.deletedSnippetCount
                            })}
                            className="p-1.5 rounded transition-all text-destructive hover:bg-destructive/10"
                            title="Delete permanently"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            // Snippets section
            deletedSnippets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                <p className="text-xs font-medium opacity-50">No deleted snippets</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {deletedSnippets.map(snippet => (
                  <div key={snippet.id} className="relative group">
                    <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-muted-foreground/20" />

                    <div className="p-4 hover:bg-muted/30 transition-colors pl-5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-muted-foreground line-clamp-1 mb-1">
                            v{snippet.version} from "{snippet.parentNoteTitle}"
                            {snippet.parentNoteDeleted && (
                              <span className="text-xs text-destructive/60 ml-1">(note deleted)</span>
                            )}
                          </h3>
                          <p className="text-xs text-muted-foreground/70 line-clamp-2 mb-1">
                            {snippet.title || snippet.content.substring(0, 60) + (snippet.content.length > 60 ? '...' : '')}
                          </p>
                          <p className="text-xs text-muted-foreground/60">
                            Deleted {formatDistanceToNow(new Date(snippet.deleted_at), { addSuffix: true })}
                          </p>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleRestoreSnippet(snippet.id)}
                            className="p-1.5 rounded transition-all text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                            title="Restore snippet"
                          >
                            <RotateCcw size={14} />
                          </button>
                          <button
                            onClick={() => setConfirmDelete({
                              type: 'snippet',
                              id: snippet.id,
                              title: `v${snippet.version} from "${snippet.parentNoteTitle}"`
                            })}
                            className="p-1.5 rounded transition-all text-destructive hover:bg-destructive/10"
                            title="Delete permanently"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onOverlayClick={() => setConfirmDelete(null)} overlayClassName="bg-black/50">
        {confirmDelete && (
          <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">
              {confirmDelete.type === 'empty'
                ? `Empty ${confirmDelete.itemType === 'notes' ? 'Notes' : 'Snippets'} Trash?`
                : `Delete ${confirmDelete.type} permanently?`
              }
            </h3>
            <div className="text-sm text-muted-foreground mb-6">
              {confirmDelete.type === 'empty' ? (
                <>
                  This will permanently delete {confirmDelete.count} {confirmDelete.itemType === 'notes' ? 'note' : 'snippet'}{confirmDelete.count !== 1 ? 's' : ''}.
                  {confirmDelete.itemType === 'notes' && ' All associated snippets will also be deleted.'}
                  <br /><br />
                  This action cannot be undone.
                </>
              ) : confirmDelete.type === 'note' ? (
                <>
                  Delete "{confirmDelete.title}" permanently?
                  <br /><br />
                  This will delete:
                  <br />• The note
                  <br />• {confirmDelete.snippetCount} snippet version{confirmDelete.snippetCount !== 1 ? 's' : ''}
                  <br /><br />
                  This action cannot be undone.
                </>
              ) : (
                <>
                  Delete {confirmDelete.title} permanently?
                  <br /><br />
                  This action cannot be undone.
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg border border-input hover:bg-accent transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmedDelete}
                className="px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors text-sm font-medium"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
